import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box, Button, Chip, Divider, IconButton, MenuItem, Paper, Popover, Stack, Table, TableBody,
    TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup,
    Tooltip, Typography,
} from "@mui/material";
import FormModal from "../../../component/common/FormModal";
import ResizableTable from "../../../component/common/ResizableTable";
import ActionButton from "../../../component/common/ActionButton";
import BaseCalendar from "../../../component/calendar/BaseCalendar";
import dayjs        from "dayjs";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { hrApi, ATTENDANCE_COLOR } from "../api/hrApi";
import AppAutocomplete from "../../../component/common/AppAutocomplete";
import { runApiAction } from "../../../util/apiFeedback";
import { ds } from "../../../styles/designSystem";
import FormField from "../../../component/common/FormField";
import LoadingDots from "../../../component/common/LoadingDots";

// ── 날짜 유틸 ────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayStr = toDateStr(new Date());

const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };

const getMonthCells = (year, month) => {
    const cells = [];
    const first = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    const offset = (first.getDay() + 6) % 7;
    for (let i = offset - 1; i >= 0; i--) cells.push({ date: toDateStr(new Date(year, month - 1, -i)), cur: false, day: new Date(year, month - 1, -i).getDate() });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ date: `${year}-${pad(month)}-${pad(d)}`, cur: true, day: d });
    while (cells.length < 42) { const d = new Date(year, month, cells.length - daysInMonth - offset + 1); cells.push({ date: toDateStr(d), cur: false, day: d.getDate() }); }
    return cells;
};

const getWeekDates = (baseDate) => {
    const d = new Date(baseDate);
    const day = d.getDay();
    const mon = addDays(d, day === 0 ? -6 : 1 - day);
    return Array.from({ length: 7 }, (_, i) => addDays(mon, i));
};

// ── 상태 동적 계산 ───────────────────────────────────────────────────────────
const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + m; };

const calcDisplayStatus = (r, setting) => {
    if (r.status === "VACATION" || r.status === "BUSINESS_TRIP" || r.status === "HALF_DAY") return r.status;
    if (!r.checkIn) return "ABSENT";
    if (!setting?.workStartTime) return r.status || "NORMAL";
    const lateAfter = toMin(setting.workStartTime) + (setting.lateGraceMinutes || 0);
    if (toMin(r.checkIn) > lateAfter) return "LATE";
    if (r.checkOut && setting.workEndTime && toMin(r.checkOut) < toMin(setting.workEndTime)) return "EARLY_LEAVE";
    return "NORMAL";
};

const calcLateMin = (r, setting) => {
    if (!r.checkIn || !setting?.workStartTime) return 0;
    const lateAfter = toMin(setting.workStartTime) + (setting.lateGraceMinutes || 0);
    return Math.max(0, toMin(r.checkIn) - lateAfter);
};

// ── CSV 내보내기 ──────────────────────────────────────────────────────────────
const exportCSV = (rows, setting, filename) => {
    const headers = ["날짜", "이름", "부서", "출근", "퇴근", "지각(분)", "상태", "메모"];
    const data = rows.map(r => {
        const st = calcDisplayStatus(r, setting);
        const sc = ATTENDANCE_COLOR[st] || ATTENDANCE_COLOR.NORMAL;
        return [r.date, r.employeeName, r.deptName || "", r.checkIn ? String(r.checkIn).slice(0, 5) : "", r.checkOut ? String(r.checkOut).slice(0, 5) : "", st === "LATE" ? calcLateMin(r, setting) : 0, sc.label, r.note || ""];
    });
    const csv = [headers, ...data].map(row => row.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
};

// ── 월간 달력: 날짜 셀 내 상태 요약 ─────────────────────────────────────────
const STATUS_ORDER = ["LATE", "ABSENT", "EARLY_LEAVE", "VACATION", "BUSINESS_TRIP", "HALF_DAY", "NORMAL"];

function DaySummary({ dayRows, setting }) {
    if (dayRows.length === 0) return null;
    const cnt = {};
    dayRows.forEach(r => { const st = calcDisplayStatus(r, setting); cnt[st] = (cnt[st] || 0) + 1; });
    const entries = STATUS_ORDER.filter(st => cnt[st]);
    return (
        <Stack spacing={0.3}>
            {entries.map(st => {
                const sc = ATTENDANCE_COLOR[st];
                return (
                    <Box key={st} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: sc.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 10, color: sc.color, fontWeight: 700, lineHeight: 1 }}>
                            {sc.label} {cnt[st]}
                        </Typography>
                    </Box>
                );
            })}
        </Stack>
    );
}

/* MonthCalendar는 BaseCalendar로 대체됨 */

const DOW_LABELS = ["월", "화", "수", "목", "금", "토", "일"];

// ── 주간 뷰 ──────────────────────────────────────────────────────────────────
function WeekTableView({ weekDates, rows, employees, setting, filterEmps, filterDepts }) {
    const byEmpDate = useMemo(() => {
        const m = {};
        rows.forEach(r => { m[`${r.employeeId}-${r.date}`] = r; });
        return m;
    }, [rows]);

    const visibleEmps = useMemo(() => {
        let list = employees;
        if (filterEmps.length > 0) list = list.filter(e => filterEmps.some(f => f.id === e.id));
        if (filterDepts.length > 0) list = list.filter(e => filterDepts.some(d => d.deptId === e.deptId));
        return list;
    }, [employees, filterEmps, filterDepts]);

    return (
        <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "160px repeat(7, minmax(0,1fr))" }}>
                <Box sx={{ py: 1.2, px: 1, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", borderRight: "1px solid #e2e8f0" }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>직원</Typography>
                </Box>
                {weekDates.map((d, i) => {
                    const ds = toDateStr(d); const isToday = ds === todayStr; const dow = i;
                    return (
                        <Box key={ds} sx={{ py: 1.2, textAlign: "center", bgcolor: isToday ? "#eff6ff" : "#f8fafc", borderBottom: "1px solid #e2e8f0", borderRight: i < 6 ? "1px solid #f1f5f9" : "none" }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: dow === 5 ? "#3b82f6" : dow === 6 ? "#ef4444" : "#64748b" }}>{DOW_LABELS[i]}</Typography>
                            <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: isToday ? "#2563eb" : "transparent", mx: "auto", mt: 0.3, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: isToday ? "#fff" : dow === 5 ? "#3b82f6" : dow === 6 ? "#ef4444" : "#1e293b" }}>{d.getDate()}</Typography>
                            </Box>
                        </Box>
                    );
                })}
                {visibleEmps.map(emp => (
                    <React.Fragment key={emp.id}>
                        <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #f1f5f9", borderRight: "1px solid #e2e8f0", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{emp.name}</Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{emp.deptName || ""}</Typography>
                        </Box>
                        {weekDates.map((d, i) => {
                            const ds = toDateStr(d); const r = byEmpDate[`${emp.id}-${ds}`];
                            if (!r) return <Box key={ds} sx={{ borderBottom: "1px solid #f1f5f9", borderRight: i < 6 ? "1px solid #f1f5f9" : "none" }} />;
                            const st = calcDisplayStatus(r, setting); const sc = ATTENDANCE_COLOR[st] || ATTENDANCE_COLOR.NORMAL;
                            const late = st === "LATE" ? calcLateMin(r, setting) : 0;
                            return (
                                <Box key={ds} sx={{ p: 0.6, borderBottom: "1px solid #f1f5f9", borderRight: i < 6 ? "1px solid #f1f5f9" : "none" }}>
                                    <Box sx={{ px: 0.75, py: 0.3, borderRadius: 0.6, bgcolor: sc.bg, borderLeft: `3px solid ${sc.color}` }}>
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: sc.color }}>{sc.label}</Typography>
                                        {r.checkIn && <Typography sx={{ fontSize: 10, color: "#64748b" }}>{String(r.checkIn).slice(0, 5)}</Typography>}
                                        {late > 0 && <Typography sx={{ fontSize: 10, color: "#dc2626", fontWeight: 700 }}>+{late}분</Typography>}
                                    </Box>
                                </Box>
                            );
                        })}
                    </React.Fragment>
                ))}
                {visibleEmps.length === 0 && (
                    <Box sx={{ gridColumn: "1 / -1", py: 5, textAlign: "center" }}>
                        <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>필터 조건에 맞는 직원이 없습니다</Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
}

// ── 일간 뷰 ──────────────────────────────────────────────────────────────────
function DayTableView({ date, rows, setting, filterEmps, filterDepts }) {
    const [sort, setSort] = useState({ key: "employeeName", dir: "asc" });

    const toggleSort = (key) =>
        setSort(s => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

    const sortLabel = (key, label) =>
        `${label}${sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ⇅"}`;

    const filtered = useMemo(() => {
        const list = rows.filter(r => {
            if (String(r.date) !== date) return false;
            if (filterEmps.length > 0 && !filterEmps.some(e => e.id === r.employeeId)) return false;
            if (filterDepts.length > 0 && !filterDepts.some(d => d.deptId === r.deptId)) return false;
            return true;
        });
        return list.sort((a, b) => {
            const av = a[sort.key] ?? "";
            const bv = b[sort.key] ?? "";
            const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv), "ko");
            return sort.dir === "asc" ? cmp : -cmp;
        });
    }, [rows, date, filterEmps, filterDepts, sort]);

    return (
        <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
            <ResizableTable size="small" sx={ds.tableSx}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("employeeName")}>{sortLabel("employeeName", "이름")}</TableCell>
                        <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("deptName")}>{sortLabel("deptName", "부서")}</TableCell>
                        <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("checkIn")}>{sortLabel("checkIn", "출근")}</TableCell>
                        <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("checkOut")}>{sortLabel("checkOut", "퇴근")}</TableCell>
                        <TableCell sx={{ color: "#dc2626" }}>지각(분)</TableCell>
                        <TableCell align="center" sx={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>{sortLabel("status", "상태")}</TableCell>
                        <TableCell>메모</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filtered.length === 0 && (
                        <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: "#94a3b8" }}>데이터 없음</TableCell></TableRow>
                    )}
                    {filtered.map(r => {
                        const st = calcDisplayStatus(r, setting); const sc = ATTENDANCE_COLOR[st] || ATTENDANCE_COLOR.NORMAL;
                        const late = st === "LATE" ? calcLateMin(r, setting) : 0;
                        return (
                            <TableRow key={r.id} hover>
                                <TableCell sx={{ fontWeight: 600 }}>{r.employeeName}</TableCell>
                                <TableCell>{r.deptName || "—"}</TableCell>
                                <TableCell>{r.checkIn ? String(r.checkIn).slice(0, 5) : "—"}</TableCell>
                                <TableCell>{r.checkOut ? String(r.checkOut).slice(0, 5) : "—"}</TableCell>
                                <TableCell sx={{ color: late > 0 ? "#dc2626" : "#94a3b8", fontWeight: late > 0 ? 700 : 400 }}>{late > 0 ? `${late}분` : "—"}</TableCell>
                                <TableCell align="center"><Chip label={sc.label} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600 }} /></TableCell>
                                <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{r.note || "—"}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </ResizableTable>
        </Paper>
    );
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────
const now = new Date();

export default function HrAttendanceTab() {
    const [viewMode, setViewMode] = useState("month");
    const [anchor, setAnchor] = useState(dayjs());
    const year  = anchor.year();
    const month = anchor.month() + 1; // 1-indexed, 기존 코드 호환
    const [selectedDate, setSelectedDate] = useState(todayStr);
    const [weekStart, setWeekStart] = useState(() => {
        const d = new Date(); const day = d.getDay();
        d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day)); return d;
    });

    const [rows, setRows] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [setting, setSetting] = useState(null);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);

    const [filterEmps, setFilterEmps] = useState([]);
    const [filterDepts, setFilterDepts] = useState([]);

    // 날짜 선택 Popover
    const [dateAnchor, setDateAnchor] = useState(null);

    const [settingDialog, setSettingDialog] = useState(false);
    const [settingForm, setSettingForm] = useState({ workStartTime: "09:00", workEndTime: "18:00", lateGraceMinutes: 0 });
    const [addDialog, setAddDialog] = useState(false);
    const [form, setForm] = useState({ employeeId: "", date: todayStr, checkIn: "", checkOut: "", note: "" });

    const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            let data;
            if (viewMode === "week") {
                const from = toDateStr(weekDates[0]); const to = toDateStr(weekDates[6]);
                data = await hrApi.getAttendanceByRange(from, to);
            } else if (viewMode === "day") {
                data = await hrApi.getAttendanceByRange(selectedDate, selectedDate);
            } else {
                data = await hrApi.getAttendance(year, month);
            }
            setRows(data || []);
        } catch { setRows([]); }
        finally { setLoading(false); }
    }, [viewMode, year, month, weekDates, selectedDate]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        Promise.allSettled([
            hrApi.getWorkSetting().catch(() => null),
            hrApi.getEmployees({ size: 9999 }).catch(() => null),
            hrApi.getDepartments().catch(() => []),
        ]).then((results) => {
            const [sRes, empResWrap, deptsRes] = results;
            const s = sRes.status === "fulfilled" ? sRes.value : null;
            const empRes = empResWrap.status === "fulfilled" ? empResWrap.value : null;
            const depts  = deptsRes.status === "fulfilled" ? deptsRes.value : [];
            if (s) { setSetting(s); setSettingForm({ workStartTime: s.workStartTime || "09:00", workEndTime: s.workEndTime || "18:00", lateGraceMinutes: s.lateGraceMinutes || 0 }); }
            const emps = empRes?.content ?? (Array.isArray(empRes) ? empRes : []);
            setEmployees(emps); setDepartments(depts || []);
        }).finally(() => setInitialLoading(false));
    }, []);

    const navigate = (dir) => {
        if (viewMode === "month") {
            setAnchor(a => a.add(dir, "month"));
        } else if (viewMode === "week") {
            setWeekStart(prev => addDays(prev, dir * 7));
        } else {
            const d = new Date(selectedDate); d.setDate(d.getDate() + dir); setSelectedDate(toDateStr(d));
        }
    };

    const goToday = () => {
        setAnchor(dayjs());
        setSelectedDate(todayStr);
        setWeekStart(() => { const d = new Date(); const day = d.getDay(); d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day)); return d; });
    };

    // 네비 레이블 클릭 → Popover 열기
    const handleLabelClick = (e) => {
        setDateAnchor(e.currentTarget);
    };

    const handlePickerSelect = (dateStr) => {
        if (!dateStr) return;
        setSelectedDate(dateStr);
        setViewMode("day");
        setDateAnchor(null);
    };

    // 월간 달력 날짜 클릭 → 일간 뷰로 이동 (BaseCalendar는 dayjs 전달)
    const handleDayClick = (day) => {
        const dateStr = day.format("YYYY-MM-DD");
        setSelectedDate(dateStr);
        setViewMode("day");
    };

    const navLabel = viewMode === "month" ? `${year}년 ${month}월`
        : viewMode === "week" ? `${toDateStr(weekDates[0])} ~ ${toDateStr(weekDates[6])}`
        : selectedDate;

    const handleSaveSetting = () => runApiAction({
        request: () => hrApi.saveWorkSetting(settingForm),
        successMsg: "근무 시간이 저장되었습니다.",
        onSuccess: async () => {
            const s = await hrApi.getWorkSetting().catch(() => null);
            if (s) setSetting(s);
        },
        closeOnSuccess: () => setSettingDialog(false),
    });

    const openAdd = (date = selectedDate) => {
        setForm({ employeeId: "", date, checkIn: "", checkOut: "", note: "" });
        setAddDialog(true);
    };

    const handleSaveAttendance = () => runApiAction({
        request: () => hrApi.saveAttendance({ ...form, employeeId: Number(form.employeeId), checkIn: form.checkIn || null, checkOut: form.checkOut || null, status: null }),
        successMsg: "근태가 등록되었습니다.",
        onSuccess: load,
        closeOnSuccess: () => setAddDialog(false),
    });

    const displayRows = useMemo(() => rows.filter(r => {
        const empOk = filterEmps.length === 0 || filterEmps.some(e => e.id === r.employeeId);
        const deptOk = filterDepts.length === 0 || filterDepts.some(d => d.deptId === r.deptId);
        return empOk && deptOk;
    }), [rows, filterEmps, filterDepts]);

    const summary = useMemo(() => {
        const cnt = {};
        displayRows.forEach(r => { const st = calcDisplayStatus(r, setting); cnt[st] = (cnt[st] || 0) + 1; });
        return cnt;
    }, [displayRows, setting]);

    const totalLate = useMemo(() =>
        displayRows.filter(r => calcDisplayStatus(r, setting) === "LATE").reduce((s, r) => s + calcLateMin(r, setting), 0),
    [displayRows, setting]);

    if (initialLoading) return <LoadingDots />;

    return (
        <Box sx={{ width: "100%", minWidth: 0 }}>
            {/* ── 필터 패널 ── */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: "1px solid #e2e8f0", mb: 2 }}>
                <Stack spacing={1.5}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                        <AppAutocomplete multiple options={employees}
                            getOptionLabel={o => o.name || ""}
                            secondaryText={o => o.deptName || ""}
                            value={filterEmps} onChange={(v) => setFilterEmps(v)}
                            label="직원 검색" placeholder="이름으로 검색..."
                            variant="outlined" disableUnderline={false}
                            sx={{ flex: 1, minWidth: 0 }} />
                        <AppAutocomplete multiple options={departments}
                            getOptionLabel={o => o.deptName || ""}
                            value={filterDepts} onChange={(v) => setFilterDepts(v)}
                            label="부서 검색" placeholder="부서명으로 검색..."
                            variant="outlined" disableUnderline={false}
                            sx={{ flex: 1, minWidth: 0 }} />
                    </Stack>
                    {setting && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <SettingsRoundedIcon sx={{ fontSize: 14, color: "#64748b" }} />
                            <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                기준 근무: <b>{setting.workStartTime}</b> ~ <b>{setting.workEndTime}</b> · 유예 <b>{setting.lateGraceMinutes}분</b>
                            </Typography>
                            <ActionButton variant="secondary" startIcon={false}
                                onClick={() => setSettingDialog(true)}
                                label="변경"
                                sx={{ fontSize: 11, color: "#2563eb", minWidth: 0, py: 0, px: 0.5,
                                    border: "none", "&:hover": { border: "none", bgcolor: "transparent" } }} />
                        </Stack>
                    )}
                </Stack>
            </Paper>

            {/* ── BaseCalendar: 네비 + 월간 그리드 ── */}
            <BaseCalendar
                anchor={anchor}
                onAnchorChange={(d) => {
                    setAnchor(d);
                    if (viewMode === "week") {
                        // d가 속한 주의 월요일로 weekStart 갱신
                        const rawDow = d.day(); // 0=일
                        const mon = d.subtract(rawDow === 0 ? 6 : rawDow - 1, "day");
                        setWeekStart(mon.toDate());
                    } else if (viewMode === "day") {
                        setSelectedDate(d.format("YYYY-MM-DD"));
                    }
                }}
                view={viewMode}
                onViewChange={(v) => { if (v) setViewMode(v); }}
                weekStartsOn="mon"
                cellHeight={90}
                onRefresh={load}
                onDayClick={handleDayClick}
                title={navLabel}
                toolbarLeft={
                    /* 날짜 제목을 클릭 가능한 Popover 버튼으로 대체 */
                    null
                }
                toolbarRight={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip title="CSV 내보내기">
                            <IconButton size="small" onClick={() => exportCSV(displayRows, setting, `근태현황_${navLabel}.csv`)}
                                sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, color: "#64748b" }}>
                                <FileDownloadRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        <ActionButton variant="secondary"
                            startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={() => openAdd()}
                            label="근태 등록" />
                    </Stack>
                }
                renderDayBadges={(day) => {
                    const dateStr = day.format("YYYY-MM-DD");
                    const dayRows = (rows.filter(r => {
                        if (String(r.date) !== dateStr) return false;
                        if (filterEmps.length  > 0 && !filterEmps.some(e => e.id === r.employeeId))  return false;
                        if (filterDepts.length > 0 && !filterDepts.some(d => d.deptId === r.deptId)) return false;
                        return true;
                    }));
                    return (
                        <>
                            {dayRows.length > 0 && (
                                <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, mb: 0.3 }}>
                                    {dayRows.length}명
                                </Typography>
                            )}
                            <DaySummary dayRows={dayRows} setting={setting} />
                        </>
                    );
                }}
            />

            {/* 날짜 선택 Popover (HR 전용) */}
            <Popover open={Boolean(dateAnchor)} anchorEl={dateAnchor} onClose={() => setDateAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                PaperProps={{ sx: { borderRadius: 1.5, p: 2, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 200 } }}>
                <Stack spacing={1}>
                    <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>날짜 선택 → 일간 이동</Typography>
                    <input type="date"
                        onChange={e => handlePickerSelect(e.target.value)}
                        style={{ fontSize: 14, padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, outline: "none", cursor: "pointer", width: "100%", boxSizing: "border-box" }} />
                </Stack>
            </Popover>

            {/* 요약 칩 */}
            <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap">
                <Typography sx={{ fontSize: 12, color: "#94a3b8", alignSelf: "center" }}>총 {displayRows.length}건</Typography>
                {Object.entries(ATTENDANCE_COLOR).map(([key, val]) => summary[key] ? (
                    <Chip key={key} size="small" label={`${val.label} ${summary[key]}`}
                        sx={{ bgcolor: val.bg, color: val.color, fontWeight: 700, fontSize: 11, borderRadius: 1 }} />
                ) : null)}
                {totalLate > 0 && (
                    <Chip size="small" label={`지각 누계 ${totalLate}분`}
                        sx={{ bgcolor: "#fef2f2", color: "#dc2626", fontWeight: 700, fontSize: 11, borderRadius: 1 }} />
                )}
            </Stack>
            {loading && viewMode !== "month" && <LoadingDots />}
            {!loading && viewMode === "week" && (
                <WeekTableView weekDates={weekDates} rows={rows} employees={employees} setting={setting}
                    filterEmps={filterEmps} filterDepts={filterDepts} />
            )}
            {!loading && viewMode === "day" && (
                <DayTableView date={selectedDate} rows={rows} setting={setting}
                    filterEmps={filterEmps} filterDepts={filterDepts} />
            )}

            {/* ── 근무설정 다이얼로그 ── */}
            <FormModal
                open={settingDialog}
                onClose={() => setSettingDialog(false)}
                title="기준 근무 시간 설정"
                maxWidth="xs"
                onSave={handleSaveSetting}
            >
                <Stack spacing={2}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <FormField label="출근" sx={{ flex: 1 }}>
                            <TextField size="small" type="time" fullWidth value={settingForm.workStartTime}
                                onChange={e => setSettingForm(p => ({ ...p, workStartTime: e.target.value }))} />
                        </FormField>
                        <FormField label="퇴근" sx={{ flex: 1 }}>
                            <TextField size="small" type="time" fullWidth value={settingForm.workEndTime}
                                onChange={e => setSettingForm(p => ({ ...p, workEndTime: e.target.value }))} />
                        </FormField>
                    </Box>
                    <FormField label="지각 유예 (분)">
                        <TextField size="small" type="number" fullWidth value={settingForm.lateGraceMinutes}
                            onChange={e => setSettingForm(p => ({ ...p, lateGraceMinutes: Number(e.target.value) }))} />
                    </FormField>
                    <Typography sx={{ fontSize: 12, color: "#64748b" }}>예) 출근 09:00, 유예 5분 → 09:06부터 지각 처리</Typography>
                </Stack>
            </FormModal>

            {/* ── 근태 등록 다이얼로그 ── */}
            <FormModal
                open={addDialog}
                onClose={() => setAddDialog(false)}
                title="근태 등록"
                maxWidth="xs"
                onSave={handleSaveAttendance}
                saveLabel="등록"
                saveDisabled={!form.employeeId || !form.date}
            >
                <Stack spacing={2}>
                    <FormField label="직원" required>
                        <AppAutocomplete options={employees}
                            getOptionLabel={o => o.name || ""}
                            secondaryText={o => o.deptName || ""}
                            onChange={(v) => setForm(p => ({ ...p, employeeId: v ? v.id : "" }))}
                            placeholder="이름으로 검색하세요"
                            variant="outlined" disableUnderline={false} />
                    </FormField>
                    <FormField label="날짜" required>
                        <TextField size="small" type="date" fullWidth value={form.date}
                            onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
                    </FormField>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <FormField label="출근" sx={{ flex: 1 }}>
                            <TextField size="small" type="time" fullWidth value={form.checkIn}
                                onChange={e => setForm(p => ({ ...p, checkIn: e.target.value }))} />
                        </FormField>
                        <FormField label="퇴근" sx={{ flex: 1 }}>
                            <TextField size="small" type="time" fullWidth value={form.checkOut}
                                onChange={e => setForm(p => ({ ...p, checkOut: e.target.value }))} />
                        </FormField>
                    </Box>
                    <FormField label="메모">
                        <TextField size="small" fullWidth value={form.note}
                            onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                            placeholder="메모를 입력하세요" />
                    </FormField>
                    <Typography sx={{ fontSize: 12, color: "#64748b" }}>· 상태는 출퇴근 시간 기준으로 자동 판단됩니다</Typography>
                </Stack>
            </FormModal>
        </Box>
    );
}
